import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";

import { swSettings } from "app/common/services/swSettings";
import type { IHDBannerCtaState } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import { changeTooltipFormatter } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChangeTooltip";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noAnimationConfig from "pro-features/components/Chart/src/configs/animation/noAnimationConfig";
import dailyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/dailyIntervalConfig";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import ChangeBellWithoutTooltip from "pro-features/components/Chart/src/configs/plotLines/ChangeBellWithoutTooltip";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";

import { getYaxisFormat } from "./ChannelAnalysisChartConfigHelpers";

export const filtersList = {
    MobileWeb: ["Direct", "Organic Search", "Paid Search", "Referrals", "Display Ads", "Social", "Email"],
    Total: [
        "Direct",
        "Search - Organic",
        "Search - Paid",
        "Referrals",
        "Display Ads",
        "Social - Organic",
        "Social - Paid",
        "Gen AI",
        "Email",
        "Affiliates",
    ],
    Desktop: ["Direct", "Organic Search", "Paid Search", "Referrals", "Display Ads", "Social", "Email"],
} as const;
const getChangeColor = (isDecrease, isNan) =>
    !isNan ? (!isDecrease ? colorsPalettes.red.s100 : colorsPalettes.green.s100) : colorsPalettes.carbon[500];

const getTooltip = (
    isPercent,
    metric,
    granularity,
    isMTDOn,
    mtdEndDate,
    periodDuration,
    isWindow,
    getHdBannerCtaState: () => IHDBannerCtaState,
) => {
    return {
        tooltip: {
            followPointer: false,
            shared: true,
            outside: true,
            useHTML: true,
            stickOnContact: true,
            style: {
                pointerEvents: "auto",
            },
            formatter: changeTooltipFormatter(
                metric,
                granularity,
                isMTDOn,
                mtdEndDate,
                isPercent,
                periodDuration,
                isWindow,
                metric === "BounceRate" ? getChangeColor : null,
                getHdBannerCtaState,
            ),
        },
    };
};
const getZones = (granularity, data, isMTDOn, mtdEndDate, durationObject) => {
    const points = data[0]?.data;
    if (points) {
        const isPartial = () => {
            if (points.length > 1) {
                const from = dayjs(points[points.length - 1][0]);
                const to = isMTDOn ? dayjs(mtdEndDate, "YYYY-MM-DD") : dayjs(durationObject.forAPI.to, "YYYY-MM-DD");
                const toDate = from.clone().add(6, "days");
                return granularity === "Weekly"
                    ? !toDate.isSame(to, "day")
                    : !isMTDOn
                      ? false
                      : dayjs.utc(mtdEndDate).isAfter(swSettings.current.endDate);
            } else {
                return false;
            }
        };
        if (granularity !== "Daily") {
            return isPartial()
                ? [{ dashStyle: "Solid", value: points[points.length - 2][0] }]
                : [
                      {
                          dashStyle: "Solid",
                          value: points[points.length - 1][0],
                      },
                  ];
        }
        return [{ dashStyle: "Solid", value: points[points.length - 1][0] }];
    } else {
        return [];
    }
};
export const getChartConfig = (
    isPercentage,
    metric,
    granularity,
    isMTDOn,
    mtdEndDate,
    durationObject,
    isPoP,
    data,
    isDesktop,
    newMMXAlgoStartData,
    getHdBannerCtaState: () => IHDBannerCtaState,
) => {
    const { from, to, isWindow } = durationObject.forAPI;
    const start = dayjs(from, "YYYY-MM-DD").format("MMM YYYY") === dayjs(to, "YYYY-MM-DD").format("MMM YYYY");

    const isDailyOrWeekly = granularity === "Daily" || granularity === "Weekly";
    const shouldUseDailyConfig = isWindow || start || isDailyOrWeekly;

    const currentGranularity = shouldUseDailyConfig ? dailyIntervalConfig : monthlyIntervalConfig;

    const yAxisFormatter = ({ value }) => getYaxisFormat(metric, value, isPercentage);
    const xAxisFormatter = ({ value }) => {
        const shouldUseMonthlyFormat = currentGranularity === monthlyIntervalConfig && !isDailyOrWeekly;
        return shouldUseMonthlyFormat ? dayjs.utc(value).format("MMM YYYY") : dayjs.utc(value).format("DD MMM");
    };
    const isPercent = isPercentage || metric === "BounceRate";
    const periodDuration = durationObject.raw.to.diff(durationObject.raw.from, "months");
    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        currentGranularity,
        noLegendConfig,
        yAxisLabelsConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        noAnimationConfig,
        tooltipContainerConfig,
        getTooltip(isPercent, metric, granularity, isMTDOn, mtdEndDate, periodDuration, isWindow, getHdBannerCtaState),
        !isDesktop &&
            from !== dayjs(newMMXAlgoStartData).format("YYYY|MM|DD") &&
            ChangeBellWithoutTooltip(dayjs(newMMXAlgoStartData)),
        {
            chart: {
                height: 295,
                type: isPercentage ? "area" : "line",
                spacingTop: 10,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                area: {
                    stacking: "percent",
                    fillOpacity: 1,
                },
                line: {
                    lineWidth: 2,
                    connectNulls: true,
                },
                series: {
                    dashStyle: "Dash",
                    marker: {
                        enabled:
                            granularity !== "Daily" &&
                            !(granularity === "Weekly" && periodDuration > 10) &&
                            !isPercentage,
                        symbol: "circle",
                    },
                    zoneAxis: "x",
                    zones: getZones(granularity, data, isMTDOn, mtdEndDate, durationObject),
                },
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: true,
                showLastLabel: true,
                reversed: false,
                gridZIndex: 2,
                reversedStacks: true,
                tickPixelInterval: 50,
                plotLines: [
                    {
                        color: "#656565",
                        width: 2,
                        value: 0,
                    },
                ],
                labels: {
                    style: {
                        textTransform: "uppercase",
                        fontSize: "11px",
                        fontFamily: "Roboto",
                    },
                },
            },
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                tickLength: 5,
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: "#919191",
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
            },
        },
    ]);
};
